<template>
  <!-- AB#21966 — one message in an order's team chat: a person, ELSA, or a system note. -->
  <div v-if="message.kind === 'system'" class="tcm-sys" :data-test="`tc-msg-${message.id}`">
    {{ message.body }}
  </div>

  <div v-else-if="message.kind === 'elsa'" class="tcm-elsa" :data-test="`tc-msg-${message.id}`">
    <AgentAvatar agent="elsa" :size="30" />
    <div class="tcm-main">
      <div class="tcm-meta"><b class="tcm-elsa-name">ELSA</b> · {{ ago(message.created_at) }}</div>
      <div class="tcm-text">{{ message.body }}</div>
      <template v-if="message.elsa">
        <div v-if="message.elsa.resolved" class="tcm-resolved" data-test="tc-elsa-resolved">
          {{
            message.elsa.resolved === 'moved'
              ? `Moved by ${message.elsa.resolved_by ?? 'a colleague'}.`
              : `${message.elsa.resolved_by ?? 'A colleague'} said it is about this order.`
          }}
        </div>
        <div v-else-if="canSend" class="tcm-acts">
          <button
            v-if="message.elsa.kind === 'other_order'"
            type="button"
            class="tcm-act tcm-act--elsa"
            data-test="tc-elsa-move"
            @click="emit('move', message.elsa.finding_id, message.elsa.other_order_no)"
          >
            Move
            {{
              message.elsa.message_count === 1
                ? '1 message'
                : `${message.elsa.message_count} messages`
            }}
            to
            {{ message.elsa.other_order_no ? `#${message.elsa.other_order_no}` : 'another order' }}
          </button>
          <button
            type="button"
            class="tcm-act"
            data-test="tc-elsa-dismiss"
            @click="emit('dismiss', message.elsa.finding_id)"
          >
            It's about this order
          </button>
        </div>
      </template>
    </div>
  </div>

  <div
    v-else
    class="tcm"
    :class="{ 'tcm--mine': mine, 'tcm--deleted': message.deleted }"
    :data-test="`tc-msg-${message.id}`"
  >
    <UserAvatar
      :name="message.user?.name"
      :initials="message.user?.initials"
      :src="message.user?.avatar_url ?? null"
      :size="30"
      tone="soft"
    />
    <div class="tcm-main">
      <div class="tcm-meta">
        <b>{{ message.user?.name ?? 'Someone' }}</b>
        <span v-if="message.user?.role"> · {{ message.user.role }}</span>
        · {{ ago(message.created_at) }}
        <span v-if="message.edited && !message.deleted" class="tcm-edited">· edited</span>
        <span v-if="portionLabel" class="tcm-portion" :title="`About ${portionLabel}`">{{
          portionLabel
        }}</span>
      </div>

      <div v-if="message.moved_from" class="tcm-moved">
        Moved here from #{{ message.moved_from.order_no }} by {{ message.moved_from.by }}
      </div>
      <div v-if="message.reply_to" class="tcm-quote" data-test="tc-quote">
        Replying to {{ message.reply_to.name }}: “{{ message.reply_to.excerpt }}”
      </div>

      <p v-if="message.deleted" class="tcm-text tcm-gone">Message removed</p>
      <template v-else-if="editing">
        <textarea
          :id="`tc-edit-${message.id}`"
          v-model="draft"
          class="tcm-edit"
          rows="2"
          aria-label="Edit message"
          data-test="tc-edit-input"
          @keydown.enter.exact.prevent="saveEdit"
          @keydown.esc="editing = false"
        ></textarea>
        <div class="tcm-acts">
          <button type="button" class="tcm-act" @click="editing = false">Cancel</button>
          <button
            type="button"
            class="tcm-act tcm-act--primary"
            data-test="tc-edit-save"
            @click="saveEdit"
          >
            Save
          </button>
        </div>
      </template>
      <template v-else>
        <p
          v-if="showOriginal || !message.body_en"
          class="tcm-text"
          :dir="isRtl(message.body) ? 'rtl' : undefined"
        >
          <template v-for="(s, i) in segments(message.body ?? '')" :key="i"
            ><span v-if="s.mention" class="tcm-mention">{{ s.text }}</span
            ><template v-else>{{ s.text }}</template></template
          >
        </p>
        <p v-else class="tcm-text" data-test="tc-translated">
          <template v-for="(s, i) in segments(message.body_en)" :key="i"
            ><span v-if="s.mention" class="tcm-mention">{{ s.text }}</span
            ><template v-else>{{ s.text }}</template></template
          >
        </p>
        <div v-if="message.body_en" class="tcm-tr">
          <LanguageIcon class="size-3.5" aria-hidden="true" />
          <span>{{
            showOriginal
              ? 'Original'
              : `Translated${message.source_language ? ` from ${message.source_language}` : ''}`
          }}</span>
          <button
            type="button"
            class="tcm-link"
            data-test="tc-toggle-original"
            @click="showOriginal = !showOriginal"
          >
            {{ showOriginal ? 'Show English' : 'Show original' }}
          </button>
        </div>
        <div
          v-else-if="canTranslate && message.body && looksNonEnglish(message.body)"
          class="tcm-tr"
        >
          <LanguageIcon class="size-3.5" aria-hidden="true" />
          <button
            type="button"
            class="tcm-link"
            data-test="tc-translate"
            @click="emit('translate', message.id)"
          >
            Translate to English
          </button>
        </div>
      </template>

      <div v-if="message.files.length" class="tcm-files">
        <button
          v-for="f in message.files"
          :key="f.id"
          type="button"
          class="tcm-file"
          :class="{ 'tcm-file--img': f.is_image && thumbs[f.id] }"
          :title="f.name"
          :data-test="`tc-file-${f.id}`"
          @click="emit('open-file', f)"
        >
          <img v-if="f.is_image && thumbs[f.id]" :src="thumbs[f.id]" :alt="f.name" />
          <template v-else>
            <span class="tcm-file-ext">{{ ext(f.name) }}</span>
            <span class="tcm-file-name">{{ f.name }}</span>
            <span class="tcm-file-size">{{ fileSize(f.size) }}</span>
          </template>
        </button>
      </div>

      <div v-if="visibleFlags.length" class="tcm-flags">
        <span
          v-for="(fl, i) in visibleFlags"
          :key="i"
          class="tcm-flag"
          :class="`tcm-flag--${fl.kind}`"
          :data-test="`tc-flag-${fl.kind}`"
          :title="flagTitle(fl)"
        >
          {{ flagText(fl) }}
        </span>
        <template v-if="canSend && actionFlag">
          <button
            v-if="actionFlag.kind === 'other_order'"
            type="button"
            class="tcm-link"
            data-test="tc-flag-move"
            @click="emit('move', actionFlag.finding_id!, actionFlag.other_order_no ?? null)"
          >
            Move to
            {{ actionFlag.other_order_no ? `#${actionFlag.other_order_no}` : 'another order' }}
          </button>
          <button
            v-if="actionFlag.kind === 'other_order' || actionFlag.kind === 'personal'"
            type="button"
            class="tcm-link"
            data-test="tc-flag-dismiss"
            @click="emit('dismiss', actionFlag.finding_id!)"
          >
            It's about this order
          </button>
        </template>
      </div>

      <div v-if="!message.deleted && !editing" class="tcm-tools">
        <button
          v-if="canSend"
          type="button"
          class="tcm-tool"
          data-test="tc-reply"
          @click="emit('reply', message)"
        >
          Reply
        </button>
        <button
          v-if="message.can_edit"
          type="button"
          class="tcm-tool"
          data-test="tc-edit"
          @click="startEdit"
        >
          Edit
        </button>
        <button
          v-if="message.can_delete"
          type="button"
          class="tcm-tool tcm-tool--danger"
          data-test="tc-delete"
          @click="emit('delete', message.id)"
        >
          Delete
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { LanguageIcon } from '@heroicons/vue/24/outline';
import AgentAvatar from '@/components/Shared/AgentAvatar.vue';
import UserAvatar from '@/components/Shared/UserAvatar.vue';
import {
  FLAG_LABELS,
  ago,
  fileSize,
  looksNonEnglish,
  type TcFile,
  type TcFlag,
  type TcMessage,
  type TcPortion,
} from '@/composables/useTeamChat';

const props = withDefaults(
  defineProps<{
    message: TcMessage;
    meId: number | null;
    canSend: boolean;
    canTranslate?: boolean;
    names?: string[];
    portions?: TcPortion[];
    /** Finding ids whose actions show on this message (the first message of each). */
    actionFindingIds?: number[];
    loadThumb?: (fileId: number) => Promise<string | null>;
  }>(),
  {
    canTranslate: true,
    names: () => [],
    portions: () => [],
    actionFindingIds: () => [],
    loadThumb: undefined,
  }
);
const emit = defineEmits<{
  (e: 'reply', m: TcMessage): void;
  (e: 'edit', id: number, body: string): void;
  (e: 'delete', id: number): void;
  (e: 'translate', id: number): void;
  (e: 'dismiss', findingId: number): void;
  (e: 'move', findingId: number, orderNo: string | null): void;
  (e: 'open-file', f: TcFile): void;
}>();

const mine = computed(() => props.meId !== null && props.message.user?.id === props.meId);
const showOriginal = ref(false);
const editing = ref(false);
const draft = ref('');
const thumbs = ref<Record<number, string>>({});

const portionLabel = computed(() => {
  if (!props.message.portion_id) return '';
  const p = props.portions.find((x) => x.id === props.message.portion_id);
  return p ? `${p.item_no}${p.factory ? ` · ${p.factory}` : ''}` : '';
});

const visibleFlags = computed(() => props.message.flags.filter((f) => !f.dismissed));
const actionFlag = computed(
  () =>
    visibleFlags.value.find(
      (f) =>
        f.finding_id !== undefined &&
        props.actionFindingIds.includes(f.finding_id) &&
        (f.kind === 'other_order' || f.kind === 'personal')
    ) ?? null
);
const flagText = (f: TcFlag): string => {
  if (f.kind === 'other_order')
    return f.other_order_no ? `Looks like #${f.other_order_no}` : FLAG_LABELS.other_order;
  if (f.kind === 'sensitive_data') return f.masked ? 'Sensitive data hidden' : 'Contact details';
  return FLAG_LABELS[f.kind];
};
const flagTitle = (f: TcFlag): string =>
  f.kind === 'sensitive_data'
    ? f.masked
      ? 'A password, card or bank number was hidden before it was saved.'
      : 'A customer’s contact details were shared here. ELSA keeps a note of it.'
    : f.kind === 'other_order'
      ? 'ELSA thinks this is about another order.'
      : f.kind === 'personal'
        ? 'ELSA thinks this is personal talk. This chat is part of the order’s record.'
        : 'ELSA noted the tone of this message.';

const isRtl = (t: string | null): boolean => !!t && /[֐-ۿ]/.test(t);
const ext = (name: string): string => (name.split('.').pop() ?? 'file').slice(0, 4).toUpperCase();

/** Split text into plain and "@Name" segments for the names in this chat. */
const segments = (text: string): { text: string; mention: boolean }[] => {
  const names = [...props.names].filter(Boolean).sort((a, b) => b.length - a.length);
  if (!names.length || !text.includes('@')) return [{ text, mention: false }];
  const esc = names.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const re = new RegExp(`@(?:${esc.join('|')})`, 'g');
  const out: { text: string; mention: boolean }[] = [];
  let last = 0;
  for (const m of text.matchAll(re)) {
    const at = m.index ?? 0;
    if (at > last) out.push({ text: text.slice(last, at), mention: false });
    out.push({ text: m[0], mention: true });
    last = at + m[0].length;
  }
  if (last < text.length) out.push({ text: text.slice(last), mention: false });
  return out;
};

const startEdit = (): void => {
  draft.value = props.message.body ?? '';
  editing.value = true;
};
const saveEdit = (): void => {
  const v = draft.value.trim();
  if (!v) return;
  emit('edit', props.message.id, v);
  editing.value = false;
};

onMounted(async () => {
  if (!props.loadThumb) return;
  for (const f of props.message.files) {
    if (!f.is_image) continue;
    const url = await props.loadThumb(f.id);
    if (url) thumbs.value = { ...thumbs.value, [f.id]: url };
  }
});
</script>

<style scoped>
.tcm,
.tcm-elsa {
  display: flex;
  gap: 10px;
  padding: 6px 8px;
  border-radius: 10px;
  position: relative;
}
.tcm:hover {
  background: var(--surface-2);
}
.tcm-main {
  min-width: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.tcm-meta {
  font-size: 12px;
  color: var(--ink-mute);
}
.tcm-meta b {
  color: var(--ink);
  font-weight: 600;
}
.tcm-edited {
  font-style: italic;
}
.tcm-portion {
  margin-left: 6px;
  font-size: 10.5px;
  font-weight: 600;
  padding: 1px 6px;
  border-radius: 5px;
  background: var(--surface-3);
  color: var(--ink-soft);
}
.tcm-text {
  margin: 0;
  font-size: 13px;
  line-height: 1.55;
  color: var(--ink);
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}
.tcm-gone {
  color: var(--ink-mute);
  font-style: italic;
}
.tcm-mention {
  color: var(--accent);
  background: var(--accent-soft);
  border-radius: 4px;
  padding: 0 3px;
  font-weight: 500;
}
.tcm-quote,
.tcm-moved {
  font-size: 11.5px;
  color: var(--ink-mute);
  border-left: 2px solid var(--border);
  padding: 1px 0 1px 8px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.tcm-tr {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: var(--ink-mute);
}
.tcm-link {
  border: 0;
  background: none;
  padding: 0;
  font-size: 11.5px;
  color: var(--accent);
  cursor: pointer;
}
.tcm-files {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.tcm-file {
  display: grid;
  grid-template-columns: auto 1fr;
  grid-template-rows: auto auto;
  column-gap: 10px;
  align-items: center;
  max-width: 260px;
  padding: 8px 12px;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--surface);
  color: var(--ink);
  text-align: left;
  cursor: pointer;
}
.tcm-file:hover {
  border-color: var(--accent);
}
.tcm-file--img {
  display: block;
  padding: 0;
  overflow: hidden;
  width: 140px;
  height: 96px;
}
.tcm-file--img img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.tcm-file-ext {
  grid-row: span 2;
  width: 32px;
  height: 36px;
  border-radius: 5px;
  background: var(--surface-3);
  color: var(--ink-soft);
  font-size: 9px;
  font-weight: 700;
  display: grid;
  place-items: center;
}
.tcm-file-name {
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.tcm-file-size {
  font-size: 11px;
  color: var(--ink-mute);
}
.tcm-flags {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}
.tcm-flag {
  font-size: 10.5px;
  font-weight: 600;
  padding: 1px 7px;
  border-radius: 5px;
  background: var(--surface-3);
  color: var(--ink-soft);
}
.tcm-flag--other_order {
  background: color-mix(in srgb, #7c5cd6 16%, var(--surface));
  color: #7c5cd6;
}
.tcm-flag--personal,
.tcm-flag--harsh_tone {
  background: var(--warning-soft);
  color: var(--warning);
}
.tcm-flag--sensitive_data {
  background: var(--danger-soft);
  color: var(--danger);
}
.tcm-tools {
  position: absolute;
  top: 2px;
  right: 6px;
  display: none;
  gap: 2px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 7px;
  padding: 2px;
}
.tcm:hover .tcm-tools,
.tcm:focus-within .tcm-tools {
  display: flex;
}
.tcm-tool {
  border: 0;
  background: transparent;
  font-size: 11.5px;
  color: var(--ink-soft);
  padding: 3px 8px;
  border-radius: 5px;
  cursor: pointer;
}
.tcm-tool:hover {
  background: var(--surface-2);
  color: var(--ink);
}
.tcm-tool--danger:hover {
  color: var(--danger);
}
.tcm-edit {
  width: 100%;
  resize: vertical;
  border: 1px solid var(--accent);
  border-radius: 8px;
  background: var(--surface);
  color: var(--ink);
  font: inherit;
  font-size: 13px;
  padding: 6px 8px;
}
.tcm-acts {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.tcm-act {
  height: 28px;
  padding: 0 10px;
  border-radius: 7px;
  border: 1px solid var(--border);
  background: transparent;
  color: var(--ink-soft);
  font-size: 12px;
  cursor: pointer;
}
.tcm-act:hover {
  color: var(--ink);
  border-color: var(--ink-mute);
}
.tcm-act--primary {
  background: var(--accent);
  border-color: var(--accent);
  color: #fff;
}
.tcm-act--elsa {
  border-color: color-mix(in srgb, #7c5cd6 45%, var(--border));
  background: color-mix(in srgb, #7c5cd6 12%, var(--surface));
  color: #7c5cd6;
}
.tcm-elsa {
  border: 1px solid color-mix(in srgb, #7c5cd6 35%, var(--border));
  background: color-mix(in srgb, #7c5cd6 7%, var(--surface));
  padding: 10px 12px;
  margin: 2px 0;
}
.tcm-elsa-name {
  color: #7c5cd6;
}
.tcm-resolved {
  font-size: 11.5px;
  color: var(--ink-mute);
}
.tcm-sys {
  align-self: center;
  text-align: center;
  font-size: 11.5px;
  color: var(--ink-mute);
  padding: 2px 10px;
}
</style>
